import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("token", data.token);
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    if (data.token) localStorage.setItem("token", data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("token");
    // Session-scoped UI state (Journal sort, etc.) must reset on logout so
    // the next login starts from the "most-recent updates on top" default
    // instead of inheriting the previous user's session picks.
    try { sessionStorage.clear(); } catch { /* private mode */ }
    setUser(false);
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  };

  // Shallow-merge patch into the current user object so callers can keep the
  // context in sync after a successful PATCH /auth/me without paying the
  // round-trip cost of `refresh()`.
  const patchUser = (patch) => {
    setUser((u) => (u && typeof u === "object" ? { ...u, ...patch } : u));
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh, patchUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
